import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { AccountService } from '../../account/account.service';
import { CustomerUserDTO } from '../../models/customer.model';
import { pollWhileVisible } from '../../runners/poll-while-visible';
import { PairedRunner, RunnerPairingService } from '../../services/runner-pairing.service';
import { TenantUserService } from '../../services/tenant-user.service';
import { isAgentAccount } from '../../shared/account-kind';
import { runnerState } from '../runner-list/runner-list.component';
import { VerwaltungBereich } from './verwaltung-bereich';

export type LadeZustand = 'laedt' | 'bereit' | 'fehler';

const RECHNER_TAKT_MS = 10_000;

/** Seitenweiter Stand der Verwaltung; je Seite eine Instanz (`providers` der Seite). */
@Injectable()
export class VerwaltungStore {
  private readonly accountService = inject(AccountService);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly runnerService = inject(RunnerPairingService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _firmenname = signal('');
  private readonly _customerId = signal<string | null>(null);
  private readonly _angemeldeteId = signal<string | null>(null);
  private readonly _angemeldeteRolle = signal<string | null>(null);
  private readonly _personen = signal<CustomerUserDTO[] | null>(null);
  private readonly _personenZustand = signal<LadeZustand>('laedt');
  private readonly _rechner = signal<PairedRunner[] | null>(null);
  private readonly _rechnerStatus = signal<LadeZustand>('laedt');
  private readonly _aktiverBereich = signal<VerwaltungBereich | null>(null);

  readonly firmenname = this._firmenname.asReadonly();
  readonly customerId = this._customerId.asReadonly();
  readonly angemeldeteId = this._angemeldeteId.asReadonly();
  readonly angemeldeteRolle = this._angemeldeteRolle.asReadonly();
  readonly personen = this._personen.asReadonly();
  readonly personenZustand = this._personenZustand.asReadonly();
  readonly rechner = this._rechner.asReadonly();
  readonly rechnerStatus = this._rechnerStatus.asReadonly();
  readonly aktiverBereich = this._aktiverBereich.asReadonly();

  readonly eingeladen = computed(() => {
    const personen = this._personen();
    return personen === null ? null : personen.filter((p) => p.status === 'PENDING' || p.status === 'EXPIRED').length;
  });

  readonly rechnerOffline = computed(() => {
    const rechner = this._rechner();
    return rechner === null ? null : rechner.filter((r) => runnerState(r) === 'offline').length;
  });

  constructor() {
    this.personenAktualisieren();
    pollWhileVisible(() => this.runnerService.listRunners(), RECHNER_TAKT_MS, inject(DOCUMENT))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((liste) => this.rechnerUebernehmen(liste));
  }

  personenAktualisieren(): void {
    if (this._personenZustand() === 'fehler') {
      this._personenZustand.set('laedt');
    }
    this.customerIdErmitteln()
      .pipe(
        switchMap((customerId) => this.tenantUserService.getUsers(customerId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (liste) => {
          this._personen.set(liste.filter((p) => !isAgentAccount(p)));
          this._personenZustand.set('bereit');
        },
        error: () => this.fehlerUebernehmen(this._personenZustand),
      });
  }

  rechnerAktualisieren(): void {
    if (this._rechnerStatus() === 'fehler') {
      this._rechnerStatus.set('laedt');
    }
    this.runnerService
      .listRunners()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (liste) => this.rechnerUebernehmen(liste),
        error: () => this.rechnerUebernehmen(null),
      });
  }

  bereichAnmelden(bereich: VerwaltungBereich): void {
    this._aktiverBereich.set(bereich);
  }

  bereichAbmelden(bereich: VerwaltungBereich): void {
    if (this._aktiverBereich() === bereich) {
      this._aktiverBereich.set(null);
    }
  }

  private customerIdErmitteln(): Observable<string> {
    const bekannt = this._customerId();
    if (bekannt !== null) {
      return of(bekannt);
    }
    return this.accountService.getProfile().pipe(
      tap((profil) => {
        this._firmenname.set(profil.customerName);
        this._customerId.set(profil.customerId);
        this._angemeldeteId.set(profil.id);
        this._angemeldeteRolle.set(profil.role);
      }),
      map((profil) => profil.customerId),
    );
  }

  private rechnerUebernehmen(liste: PairedRunner[] | null): void {
    if (liste === null) {
      this.fehlerUebernehmen(this._rechnerStatus);
      return;
    }
    this._rechner.set(liste);
    this._rechnerStatus.set('bereit');
  }

  /** Ein Fehler nach dem ersten Erfolg lässt den letzten Stand stehen. */
  private fehlerUebernehmen(zustand: WritableSignal<LadeZustand>): void {
    if (zustand() !== 'bereit') {
      zustand.set('fehler');
    }
  }
}
